'use client';

import { FunctionComponent, SVGProps, useEffect } from 'react';

import { stack } from '@flipster/styled-system/patterns';
import segment, { ChartEvent } from '@flipster/utils/segment';

import { SeriesType } from '../../lib/charting_library';
import { useTradingViewDataStorage } from '../misc/data-manager';
import { useTvHeaderContext } from './';
import { Button, CheckItem } from './button';
import { Dropdown } from './dropdown';
import AreaIcon from './icons/area-icon.svg';
import BarsIcon from './icons/bars-icon.svg';
import BaselineIcon from './icons/baseline-icon.svg';
import CandlesIcon from './icons/candles-icon.svg';
import ColumnsIcon from './icons/columns-icon.svg';
import HeikinAshiIcon from './icons/heikin-ashi-icon.svg';
import HighLowIcon from './icons/high-low-icon.svg';
import HLCAreaIcon from './icons/hlc-area-icon.svg';
import HollowCandlesIcon from './icons/hollow-candles-icon.svg';
import LineIcon from './icons/line-icon.svg';
import LineWithMarkersIcon from './icons/line-with-markers-icon.svg';
import StepLineIcon from './icons/step-line-icon.svg';

export const Candles = () => {
  const tv = useTvHeaderContext();

  const [chartType, setChartType] = useTradingViewDataStorage({
    key: 'chartType',
  });

  useEffect(() => {
    const handler = async () => {
      tv.activeChart?.setChartType(await chartType.asyncValue());
    };
    return tv.on('onFirstDataLoaded', handler);
  }, [chartType, tv]);

  const currentChartType = chartType.value;
  const CurrentChartTypeIcon = ChartTypeMapper[currentChartType];

  return (
    <Dropdown
      withClickClose
      trigger={<Button>{CurrentChartTypeIcon && <CurrentChartTypeIcon width={24} height={24} />}</Button>}
    >
      <div className={stack({ gap: 6 })}>
        {ChartTypes.map((ct) => (
          <CheckItem
            checked={ct.value === currentChartType}
            value={ct.value}
            onClick={() => {
              if (!tv.enabled) return;
              setChartType(ct.value);
              tv.activeChart?.setChartType(ct.value);
              segment.track(ChartEvent.CandlesSelectClick, { chartType: ct.value });
            }}
            key={ct.label}
          >
            <ct.icon width={24} height={24} />
            {ct.label}
          </CheckItem>
        ))}
      </div>
    </Dropdown>
  );
};

const ChartTypes = [
  {
    label: 'Bars',
    icon: BarsIcon,
    value: SeriesType.Bars,
  },
  {
    label: 'Candles',
    icon: CandlesIcon,
    value: SeriesType.Candles,
  },
  {
    label: 'Hollow Candles',
    icon: HollowCandlesIcon,
    value: SeriesType.HollowCandles,
  },
  {
    label: 'Line',
    icon: LineIcon,
    value: SeriesType.Line,
  },
  {
    label: 'Line with Markers',
    icon: LineWithMarkersIcon,
    value: SeriesType.LineWithMarkers,
  },
  {
    label: 'Step line',
    icon: StepLineIcon,
    value: SeriesType.Stepline,
  },
  {
    label: 'Area',
    icon: AreaIcon,
    value: SeriesType.Area,
  },
  {
    label: 'HLC area',
    icon: HLCAreaIcon,
    value: SeriesType.HLCArea,
  },
  {
    label: 'Baseline',
    icon: BaselineIcon,
    value: SeriesType.Baseline,
  },
  {
    label: 'Columns',
    icon: ColumnsIcon,
    value: SeriesType.Column,
  },
  {
    label: 'High-low',
    icon: HighLowIcon,
    value: SeriesType.HiLo,
  },
  {
    label: 'Heikin Ashi',
    icon: HeikinAshiIcon,
    value: SeriesType.HeikenAshi,
  },
];

type ChartTypeMapperType = Partial<Record<SeriesType, FunctionComponent<SVGProps<SVGSVGElement>>>>;

const ChartTypeMapper = ChartTypes.reduce((acc: ChartTypeMapperType, { value, icon }) => {
  acc[value] = icon;
  return acc;
}, {});
